import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://vp.matt-tye.com';

function SessionBrowser() {
  const [sessionCode, setSessionCode] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [labels, setLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [renderedImage, setRenderedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSession = async () => {
    if (!sessionCode.trim()) return;
    setLoading(true);
    setError(null);
    setSelectedLabel(null);
    setRenderedImage(null);
    try {
      const res = await fetch(`${API_URL}/sessions/${encodeURIComponent(sessionCode)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setLabels(data.labels || []);
      setActiveSession(sessionCode);
    } catch (err) {
      setError(err.message);
      setLabels([]);
    } finally {
      setLoading(false);
    }
  };

  const renderLabel = async (labelId) => {
    setSelectedLabel(labelId);
    setRenderedImage(null);
    try {
      const res = await fetch(`${API_URL}/virtual-printer/${encodeURIComponent(activeSession)}/${labelId}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setRenderedImage(url);
    } catch (err) {
      setError(`Render failed: ${err.message}`);
    }
  };

  const deleteLabel = async (labelId) => {
    try {
      const res = await fetch(`${API_URL}/sessions/${encodeURIComponent(activeSession)}/${labelId}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      if (selectedLabel === labelId) {
        setSelectedLabel(null);
        setRenderedImage(null);
      }
      await loadSession();
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const clearSession = async () => {
    if (!window.confirm(`Clear all labels from session "${activeSession}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/sessions/${encodeURIComponent(activeSession)}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setActiveSession(null);
      setLabels([]);
      setSelectedLabel(null);
      setRenderedImage(null);
      setSessionCode('');
    } catch (err) {
      setError(`Clear failed: ${err.message}`);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString();
  };

  useEffect(() => {
    return () => {
      if (renderedImage) URL.revokeObjectURL(renderedImage);
    };
  }, [renderedImage]);

  if (!activeSession) {
    return (
      <div className="session-browser">
        <div className="session-input-card">
          <h2>Session Browser</h2>
          <p>Enter a session code to view and print labels</p>
          <div className="session-input-row">
            <input
              type="text"
              value={sessionCode}
              onChange={(e) => setSessionCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadSession()}
              placeholder="Enter session code..."
              className="session-input"
            />
            <button onClick={loadSession} disabled={loading} className="btn btn-primary">
              {loading ? 'Loading...' : 'Load Session'}
            </button>
          </div>
          {error && <div className="session-error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="session-browser">
      <div className="session-header">
        <div className="session-info">
          <h2 style={{ margin: 0, fontSize: '1.125rem' }}>Session: <span className="session-code-text">{activeSession}</span></h2>
          <span className="label-count">{labels.length} label{labels.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="session-actions">
          <button onClick={() => { setActiveSession(null); setLabels([]); setSelectedLabel(null); setRenderedImage(null); }} className="btn btn-secondary">
            Back
          </button>
          <button onClick={loadSession} className="btn btn-secondary">
            Refresh
          </button>
          <button onClick={clearSession} className="btn btn-danger">
            Clear Session
          </button>
        </div>
      </div>

      {error && <div className="session-error">{error}</div>}

      <div className="session-content-grid">
        <div className="label-list-panel">
          {labels.length === 0 ? (
            <div className="empty-state">
              <p>No labels in this session</p>
              <p className="empty-hint">Send ZPL to <code>POST {API_URL}/pstprnt/{activeSession}</code></p>
            </div>
          ) : (
            <ul className="label-list">
              {labels.map((label, index) => (
                <li
                  key={label.id}
                  className={`label-item ${selectedLabel === label.id ? 'selected' : ''}`}
                  onClick={() => renderLabel(label.id)}
                >
                  <div className="label-info">
                    <span className="label-number">Label #{labels.length - index}</span>
                    <span className="label-id">ID: {label.id}</span>
                  </div>
                  <span className="label-time">{formatDate(label.created_at)}</span>
                  <button
                    className="label-delete-btn"
                    onClick={(e) => { e.stopPropagation(); deleteLabel(label.id); }}
                    title="Delete label"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="preview-panel-session">
          {renderedImage ? (
            <div className="preview-container">
              <img src={renderedImage} alt="Label preview" className="label-preview-img" />
              <a href={renderedImage} download={`label-${selectedLabel}.png`} className="btn btn-primary download-btn">
                Download PNG
              </a>
            </div>
          ) : (
            <div className="preview-placeholder">
              <p>Click a label to preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SessionBrowser;
